import React, { useEffect, useState } from "react";
import moment from "moment";
import { getClasses, createClass, updateClass } from "../services/classService";
import { getLessons } from "../services/lessonService";
import { getCourses } from "../services/courseService";
import { getStudents, getTutors } from "../services/userService";
import {
  Card,
  ListGroup,
  Button,
  Modal,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import CalendarView from "../components/ClassesCalendar";
import Loader from "../components/Loader";

const Classes = ({ canAdd = true }) => {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedClassDetail, setSelectedClassDetail] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    date: "",
    lesson: null,
    course: null,
    tutor: null,
    students: [],
  });
  const [lessons, setLessons] = useState([]);
  const [courses, setCourses] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const [clsData, lessonData, courseData, tutorData, studentData] =
      await Promise.all([
        getClasses(),
        getLessons(),
        getCourses(),
        getTutors(),
        getStudents(),
      ]);
    setLoading(false);
    const mappedEvents = clsData.map((cls) => ({
      id: cls.id,
      title: cls.name,
      start: new Date(cls.date),
      end: new Date(cls.date),
      allDay: true,
      data: cls,
    }));

    setEvents(mappedEvents);
    setLessons(lessonData);
    setCourses(courseData);
    setTutors(tutorData);
    setStudents(studentData);

    // Preselect today's date & classes
    const today = new Date();
    const todayClasses = mappedEvents
      .filter((evt) => evt.start.toDateString() === today.toDateString())
      .map((evt) => evt.data);

    setSelectedDate(today);
    setFormData((prev) => ({
      ...prev,
      date: moment(today).format("YYYY-MM-DD"),
    }));
    setSelectedClasses(todayClasses);
    if (todayClasses.length > 0) {
      setSelectedClassDetail(todayClasses[0]);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSelectDate = (slotInfo) => {
    const date = moment(slotInfo.start).format("YYYY-MM-DD");
    setSelectedDate(moment(slotInfo.start).startOf("day").toDate());
    const filtered = events
      .filter((ev) => moment(ev.start).format("YYYY-MM-DD") === date)
      .map((ev) => ev.data);
    setSelectedClasses(filtered);
    setSelectedClassDetail(null);
  };

  const handleSave = async () => {
    const fullDateTime =
      formData.date || moment(selectedDate).format("YYYY-MM-DDTHH:mm");

    await createClass({
      ...formData,
      date: fullDateTime, // This will store both date and time
    });
    setShowModal(false);
    fetchData();
  };

  const handleSelectEvent = (event) => {
    setSelectedDate(moment(event.start).startOf("day").toDate());
    setSelectedClasses([event.data]);
    setSelectedClassDetail(event.data);
  };

  return (
    <div
      className={
        canAdd
          ? "grid grid-cols-1 md:grid-cols-3 gap-4 p-4"
          : "grid grid-cols-1 md:grid-cols-3 gap-4"
      }
    >
      {loading && <Loader />}
      {/* Calendar */}
      <div className="col-span-2 bg-white dark:bg-gray-800 p-3 rounded shadow relative">
        <CalendarView
          events={events}
          selectedDate={selectedDate}
          onSelectDate={handleSelectDate}
          onSelectEvent={handleSelectEvent}
        />
      </div>

      {/* Right Panel */}
      <div className="col-span-1 space-y-4">
        {/* Show Add button only if allowed */}
        {canAdd && (
          <div className="flex justify-end">
            <Button
              className="mt-2"
              variant="primary"
              size="sm"
              onClick={() => {
                setFormData({
                  name: "",
                  description: "",
                  date: selectedDate || "",
                  lesson: null,
                  course: null,
                  tutor: null,
                  students: [],
                });
                setShowModal(true);
              }}
            >
              + Add Class
            </Button>
          </div>
        )}
        {selectedDate && (
          <Card>
            <Card.Header>
              <span className="font-bold">
                Classes on {moment(selectedDate).format("DD MMM YYYY")}
              </span>
            </Card.Header>
            <ListGroup variant="flush">
              {selectedClasses.length > 0 ? (
                selectedClasses
                  .sort(
                    (a, b) =>
                      moment(a.date).valueOf() - moment(b.date).valueOf()
                  ) // sort by time
                  .map((cls) => (
                    <ListGroup.Item
                      key={cls.id}
                      action
                      onClick={() => setSelectedClassDetail(cls)}
                    >
                      <div className="flex justify-between">
                        <span>{cls.name}</span>
                        <small className="text-muted">
                          {moment(cls.date).format("hh:mm A")}
                        </small>
                      </div>
                    </ListGroup.Item>
                  ))
              ) : (
                <div className="p-4 text-sm text-center">
                  <ListGroup.Item>No classes scheduled</ListGroup.Item>
                </div>
              )}
            </ListGroup>
          </Card>
        )}

        {selectedClassDetail && (
          <Card>
            <Card.Header>Class Details</Card.Header>
            <Card.Body>
              <h5>{selectedClassDetail.name}</h5>
              <p>{selectedClassDetail.description}</p>
              <p>
                <strong>Lesson:</strong> {selectedClassDetail.lesson?.name}
              </p>
              <p>
                <strong>Course:</strong> {selectedClassDetail.course?.name}
              </p>
              <p>
                <strong>Tutor:</strong> {selectedClassDetail.tutor?.name}
              </p>
              <p>
                <strong>Students Attended:</strong>
              </p>
              <ul>
                {selectedClassDetail.students?.length > 0 ? (
                  selectedClassDetail.students.map((stu) => (
                    <li key={stu.id}>{stu.name}</li>
                  ))
                ) : (
                  <li>No students marked attended</li>
                )}
              </ul>
            </Card.Body>
          </Card>
        )}
      </div>

      {/* Modal for Add/Edit */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Class</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                value={formData.name}
                placeholder="Class Name"
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                placeholder="Class Description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Form.Group>

            {/* Lesson & Course in same row */}
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Lesson</Form.Label>
                  <Form.Select
                    value={formData.lesson?.id || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        lesson:
                          lessons.find((l) => l.id === e.target.value) || null,
                      })
                    }
                  >
                    <option value="">Select Lesson</option>
                    {lessons.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Course</Form.Label>
                  <Form.Select
                    value={formData.course?.id || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        course:
                          courses.find((c) => c.id === e.target.value) || null,
                      })
                    }
                  >
                    <option value="">Select Course</option>
                    {courses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Tutor & Date in same row */}
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Tutor</Form.Label>
                  <Form.Select
                    value={formData.tutor?.id || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        tutor:
                          tutors.find((t) => t.id === e.target.value) || null,
                      })
                    }
                  >
                    <option value="">Select Tutor</option>
                    {tutors.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={formData.date || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Students full width */}
            <Form.Group className="mb-3">
              <Form.Label>Students</Form.Label>
              <div className="grid grid-cols-2 gap-2">
                {students.map((stu) => (
                  <Form.Check
                    key={stu.id}
                    type="checkbox"
                    label={stu.name}
                    checked={formData.students.some((s) => s.id === stu.id)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        students: e.target.checked
                          ? [...formData.students, stu]
                          : formData.students.filter((s) => s.id !== stu.id),
                      })
                    }
                  />
                ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Classes;
